// "Change Xero contact" (Session BCG): clear or change which Xero contact a customer is linked to.
//
// WHY. merge_customers refuses two customers linked to different Xero contacts and says to clear the stale link, and
// until now nothing could. This is that control. It changes ONLY the platform's stored link: it never writes to Xero (the
// contact is read once, to confirm it is there and active). Office and admin.
//
// FLOW. It never writes on open. It asks the server what the customer is linked to now (a clear preview, which also
// answers "not linked" in plain words), lets the person choose to relink or remove the link and say why, previews that
// choice (the server reads the named contact and refuses what the commit would refuse), and only then offers the
// branded two-step confirm, which names the customer and the contact. RULE 1: it is clearable back to "no link", and
// it says plainly what the customer is linked to now.
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { XeroLinkPlan, XeroLinkPreviewResult, XeroLinkCommitResult } from "../../lib/api";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";

type Choice = "relink" | "clear";

function describeCurrent(c: XeroLinkPlan["current"]): string {
  if (!c) return "No Xero contact";
  const name = c.name ? `"${c.name}"` : "a contact Xero could not name";
  return `${name} (${c.xeroContactId})${c.status && c.status !== "ACTIVE" ? `, ${c.status.toLowerCase()} in Xero` : ""}`;
}

export function XeroContactLinkModal({
  customerId,
  customerName,
  onClose,
  onChanged,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [current, setCurrent] = useState<XeroLinkPlan["current"]>(null);
  const [choice, setChoice] = useState<Choice>("relink");
  const [contactId, setContactId] = useState("");
  const [reason, setReason] = useState("");
  const [plan, setPlan] = useState<XeroLinkPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      // A clear preview is the cheapest honest read of the current link: the server answers it, name included.
      const res = (await api.customers.xeroLink(customerId, { action: "clear", preview: true })) as XeroLinkPreviewResult;
      setCurrent(res.plan.current);
      setState("ready");
    } catch (err) {
      if (err instanceof ApiError && err.body?.error === "not_linked") {
        setCurrent(null);
        setChoice("relink");
        setState("ready");
        return;
      }
      setError(err instanceof ApiError ? err.message : "Could not read the customer's Xero contact link.");
      setState("error");
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  function request(preview: boolean) {
    return api.customers.xeroLink(customerId, {
      action: choice,
      ...(choice === "relink" ? { xeroContactId: contactId.trim() } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
      expectedCurrentXeroContactId: current?.xeroContactId ?? null,
      preview,
    });
  }

  async function review() {
    setBusy(true);
    setError(null);
    setPlan(null);
    try {
      const res = (await request(true)) as XeroLinkPreviewResult;
      setPlan(res.plan);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not check that change. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    setBusy(true);
    setError(null);
    try {
      const res = (await request(false)) as XeroLinkCommitResult;
      setDone(
        res.plan.action === "clear"
          ? `${customerName} is no longer linked to a Xero contact.`
          : `${customerName} is now linked to the Xero contact "${res.plan.next?.name ?? contactId}".`,
      );
      setPlan(null);
      onChanged?.();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not change the Xero contact link.");
    } finally {
      setBusy(false);
    }
  }

  const canReview = !busy && reason.trim().length >= 5 && (choice === "clear" ? Boolean(current) : contactId.trim().length > 0);

  return (
    <ModalShell ariaLabel="Change Xero contact" onClose={() => !busy && onClose()} width="max-w-[600px]">
      <h2 className="font-heading text-xl tracking-wide text-ele-strong">Change Xero contact</h2>
      <p className="mt-1 text-sm text-ele-body">
        This changes which Xero contact {customerName} is linked to in the platform. It does not create, rename or delete anything in Xero.
      </p>

      {state === "loading" && <p className="mt-5 text-sm text-ele-body">Checking the current link...</p>}

      {state === "ready" && (
        <>
          <div className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
            <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body">Linked to now</div>
            <div className="mt-1 break-words text-sm text-ele-strong" data-testid="xero-link-current">
              {describeCurrent(current)}
            </div>
          </div>

          {!done && (
            <div className="mt-5 space-y-4">
              <fieldset>
                <legend className="font-menu text-[10px] uppercase tracking-wide text-ele-body">What to do</legend>
                <label className="mt-2 flex items-center gap-2 text-sm text-ele-body">
                  <input
                    type="radio"
                    name="xero-link-choice"
                    checked={choice === "relink"}
                    onChange={() => {
                      setChoice("relink");
                      setPlan(null);
                    }}
                  />
                  Link to a different Xero contact
                </label>
                <label className={`mt-1 flex items-center gap-2 text-sm ${current ? "text-ele-body" : "text-ele-muted"}`}>
                  <input
                    type="radio"
                    name="xero-link-choice"
                    checked={choice === "clear"}
                    disabled={!current}
                    onChange={() => {
                      setChoice("clear");
                      setPlan(null);
                    }}
                  />
                  Remove the link (no Xero contact)
                </label>
              </fieldset>

              {choice === "relink" && (
                <div>
                  <label htmlFor="xero-link-id" className="font-menu text-[10px] uppercase tracking-wide text-ele-body">
                    Xero contact id
                  </label>
                  <input
                    id="xero-link-id"
                    value={contactId}
                    onChange={(e) => {
                      setContactId(e.target.value);
                      setPlan(null);
                    }}
                    placeholder="70f211a0-4560-40ee-83b5-2bed659c47cd"
                    className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong"
                  />
                  <p className="mt-1 text-xs text-ele-body">
                    In Xero, open the contact: the id is the long code at the end of its address in the browser.
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="xero-link-reason" className="font-menu text-[10px] uppercase tracking-wide text-ele-body">
                  Why (kept in the audit log)
                </label>
                <input
                  id="xero-link-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={300}
                  placeholder="For example: duplicate contact archived in Xero"
                  className="mt-1 w-full rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong"
                />
              </div>
            </div>
          )}
        </>
      )}

      {plan && !done && (
        <div className="mt-5 rounded-ele border border-ele-green bg-ele-green/10 p-4 text-sm text-ele-body" data-testid="xero-link-plan">
          {plan.effects.map((e) => (
            <p key={e} className="mt-1 first:mt-0">
              {e}
            </p>
          ))}
          {plan.cautions.length > 0 && (
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs">
              {plan.cautions.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {done && (
        <p role="status" className="mt-4 rounded-ele border border-ele-green bg-ele-green/10 px-4 py-3 text-sm text-ele-green">
          {done}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 text-sm text-ele-danger">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button
          onClick={onClose}
          disabled={busy}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          {done ? "Close" : "Cancel"}
        </button>
        {state === "error" && (
          <button
            onClick={() => void load()}
            className={`rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset ${MODAL_ACTION_BTN}`}
          >
            Try again
          </button>
        )}
        {state === "ready" && !done && !plan && (
          <button
            onClick={() => void review()}
            disabled={!canReview}
            className={`rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset disabled:opacity-50 ${MODAL_ACTION_BTN}`}
          >
            {busy ? "Checking..." : "Check this change"}
          </button>
        )}
        {state === "ready" && !done && plan && (
          <ConfirmButton
            onConfirm={() => void commit()}
            label={plan.action === "clear" ? "Remove the Xero link" : "Change the Xero contact"}
            confirmLabel={
              plan.action === "clear"
                ? `Press again to remove ${customerName}'s Xero link`
                : `Press again to link ${customerName} to "${plan.next?.name ?? "that contact"}"`
            }
            busy={busy}
            busyLabel="Saving..."
            className={`rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green transition hover:bg-ele-inset ${MODAL_ACTION_BTN}`}
          />
        )}
      </div>
    </ModalShell>
  );
}
